'use client';
import React, { useEffect, useState, Suspense, useCallback, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Search, MapPin, BadgeCheck, Package, ArrowLeft, Sparkles } from 'lucide-react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface Supplier {
  id: string;
  company_name: string | null;
  full_name: string;
  industry: string | null;
  city: string | null;
  department: string | null;
  what_we_sell: string | null;
  description: string | null;
  logo_url: string | null;
  product_images: string[] | null;
  admin_verified: boolean | null;
  profile_completed_pct: number | null;
}

interface ScoredSupplier extends Supplier {
  _score: number;
}

// ── Stop words ──
const STOP_WORDS = new Set([
  'para', 'como', 'una', 'uno', 'los', 'las', 'del', 'con', 'por', 'que',
  'más', 'muy', 'hay', 'son', 'ser', 'está', 'han', 'sin', 'sobre', 'entre',
  'desde', 'hasta', 'hacia', 'ante', 'bajo', 'tras', 'según', 'durante',
  'esto', 'este', 'esta', 'estos', 'estas', 'ese', 'esa', 'esos', 'esas',
  'todo', 'toda', 'todos', 'todas', 'quien', 'quienes', 'cual', 'cuales',
  'me', 'mi', 'mis', 'tu', 'tus', 'su', 'sus', 'nos', 'les',
  'quiero', 'necesito', 'hacer', 'tener', 'debe', 'puede', 'tiene', 'tienen',
  'busco', 'buscar', 'busca', 'buscan', 'vende', 'venden', 'vender',
  'comprar', 'conseguir', 'encontrar', 'diga', 'dime', 'dame', 'muestra',
  'proveedor', 'proveedores', 'servicio', 'servicios', 'empresa', 'empresas',
  'producto', 'productos',
]);

const BUILT_IN_SYNONYMS: Record<string, string[]> = {
  'fumigacion': ['control de plagas', 'exterminacion', 'pest control', 'desinfeccion'],
  'herreria': ['metal', 'estructuras metalicas', 'metalmecanica', 'hierro', 'acero'],
  'cajas': ['empaques', 'packaging', 'carton', 'envases', 'embalaje'],
  'carpinteria': ['madera', 'woodworking', 'muebles', 'ebanisteria'],
  'limpieza': ['aseo', 'higiene', 'sanitizacion', 'desinfeccion'],
  'transporte': ['logistica', 'flete', 'envio', 'mensajeria', 'carga'],
  'construccion': ['obra', 'edificacion', 'arquitectura', 'civil'],
  'quimicos': ['reactivos', 'solventes', 'laboratorio', 'insumos quimicos'],
  'textil': ['telas', 'confeccion', 'ropa', 'uniformes', 'tejidos'],
  'alimentos': ['comida', 'gastronomia', 'ingredientes', 'insumos alimentarios'],
  'seguridad': ['vigilancia', 'proteccion', 'guardia', 'monitoreo'],
  'tecnologia': ['informatica', 'sistemas', 'software', 'hardware', 'it'],
  'electricidad': ['electrico', 'iluminacion', 'instalaciones electricas'],
  'plomeria': ['fontaneria', 'tuberias', 'hidraulica', 'sanitario'],
  'mecanica': ['autopartes', 'repuestos', 'mantenimiento vehicular', 'taller'],
  'acero': ['hierro', 'metal', 'acero inoxidable', 'laminas'],
  'plastico': ['polimeros', 'pvc', 'polietileno', 'moldes'],
  'fierro': ['hierro', 'acero', 'metal', 'herreria', 'metalmecanica'],
};

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').trim();
}

function extractKeywords(text: string): string[] {
  return normalize(text).split(/\s+/).filter(w => w.length > 2 && !STOP_WORDS.has(w));
}

function expandWithSynonyms(keywords: string[], synonymMap: Record<string, string[]>): string[] {
  const expanded = new Set(keywords);
  for (const kw of keywords) {
    const syns = synonymMap[kw] || BUILT_IN_SYNONYMS[kw] || [];
    for (const syn of syns) {
      normalize(syn).split(/\s+/).filter(w => w.length > 2).forEach(w => expanded.add(w));
    }
  }
  return Array.from(expanded);
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    }
  }
  return dp[m][n];
}

function fuzzyMatch(keyword: string, text: string): boolean {
  const words = text.split(/\s+/);
  const maxDist = keyword.length <= 4 ? 1 : keyword.length <= 7 ? 2 : 3;
  return words.some(w => w.length > 2 && levenshtein(keyword, w) <= maxDist);
}

function scoreSupplier(supplier: Supplier, keywords: string[], expandedKeywords: string[]): number {
  if (keywords.length === 0) return 1;
  const fields = [
    { text: normalize(supplier.company_name || ''), weight: 4 },
    { text: normalize(supplier.industry || ''), weight: 4 },
    { text: normalize(supplier.what_we_sell || ''), weight: 3 },
    { text: normalize(supplier.description || ''), weight: 2 },
    { text: normalize(supplier.city || ''), weight: 2 },
    { text: normalize(supplier.department || ''), weight: 1 },
  ];
  let score = 0;
  for (const kw of expandedKeywords) {
    for (const field of fields) {
      if (!field.text) continue;
      if (field.text.includes(kw)) {
        score += field.weight;
        if (field.text.split(/\s+/).includes(kw)) score += field.weight;
      } else if (kw.length >= 4 && fuzzyMatch(kw, field.text)) {
        score += Math.floor(field.weight / 2);
      }
    }
  }
  const pct = supplier.profile_completed_pct || 0;
  if (pct >= 80) score += 3;
  else if (pct >= 50) score += 1;
  if (supplier.admin_verified) score += 2;
  return score;
}

function hashQuery(q: string): string {
  let h = 0;
  for (let i = 0; i < q.length; i++) { h = (Math.imul(31, h) + q.charCodeAt(i)) | 0; }
  return h.toString(36);
}

function SearchResults() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const query = searchParams.get('q') || '';
  const [searchText, setSearchText] = useState(query);
  const [suppliers, setSuppliers] = useState<ScoredSupplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [synonymMap, setSynonymMap] = useState<Record<string, string[]>>(BUILT_IN_SYNONYMS);
  const supabase = createClient();
  const cacheRef = useRef<Map<string, ScoredSupplier[]>>(new Map());

  useEffect(() => {
    setSearchText(query);
  }, [query]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const nextQuery = searchText.trim();
    router.push(nextQuery ? `/search?q=${encodeURIComponent(nextQuery)}` : '/search');
  };

  // Load synonyms from DB once — not on every keystroke
  useEffect(() => {
    supabase.from('business_synonyms').select('term, synonyms').eq('is_active', true).then(({ data }) => {
      if (data) {
        const map: Record<string, string[]> = { ...BUILT_IN_SYNONYMS };
        data.forEach((row: any) => { map[normalize(row.term)] = row.synonyms; });
        setSynonymMap(map);
      }
    });
  }, []);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const cacheKey = hashQuery(normalize(query));
        if (cacheRef.current.has(cacheKey)) {
          setSuppliers(cacheRef.current.get(cacheKey)!);
          setLoading(false);
          return;
        }

        const keywords = extractKeywords(query);
        const expandedKeywords = expandWithSynonyms(keywords, synonymMap);

        let scored: ScoredSupplier[] = [];

        // PostgreSQL full-text + ilike search — no AI per keystroke
        const { data, error } = await supabase.rpc('list_supplier_directory', { p_limit: 40 });

        if (!error && data) {
          scored = data
            .map(s => ({ ...(s as Supplier), _score: scoreSupplier(s as Supplier, keywords, expandedKeywords) }))
            .filter(s => s._score > 0)
            .sort((a, b) => b._score - a._score)
            .slice(0, 40);
        }

        if (scored.length >= 0) {
          cacheRef.current.set(cacheKey, scored);
          setSuppliers(scored);

          const hadResults = scored.length > 0;
          // Fire-and-forget analytics — never blocks search
          supabase.from('search_analytics').insert({
            query_text: query,
            normalized_query: normalize(query),
            result_count: scored.length,
            had_results: hadResults,
            search_level: expandedKeywords.length > keywords.length ? 'synonym' : 'native',
          }).then(() => {});

          if (!hadResults && query.trim()) {
            const normQ = normalize(query).slice(0, 200);
            supabase.from('demand_gaps').upsert({
              normalized_query: normQ,
              query_text: query.slice(0, 200),
              search_count: 1,
              last_searched_at: new Date().toISOString(),
            }, { onConflict: 'normalized_query,city', ignoreDuplicates: false }).then(() => {});
          }
        } else {
          setSuppliers([]);
        }
      } catch (caughtError) {
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, [query, synonymMap]);

  return (
    <div className="min-h-screen bg-white">
      <Topbar />
      <div className="pt-14">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-10">
          {/* Back + header */}
          <div className="mb-8">
            <Link href="/" className="inline-flex items-center gap-1.5 text-sm text-[#6e6e73] hover:text-black transition-colors mb-4">
              <ArrowLeft size={14} />
              Volver al inicio
            </Link>
            <h1 className="text-2xl font-black text-black tracking-tight">
              {query ? `Resultados para "${query}"` : 'Todos los proveedores'}
            </h1>
            {!loading && (
              <p className="text-sm text-[#6e6e73] mt-1">
                {suppliers.length === 0
                  ? 'Sin resultados'
                  : `${suppliers.length} proveedor${suppliers.length !== 1 ? 'es' : ''} encontrado${suppliers.length !== 1 ? 's' : ''}`}
              </p>
            )}

            <form onSubmit={submitSearch} className="mt-5 flex flex-col lg:flex-row gap-2 max-w-4xl">
              <label className="relative flex-1">
                <span className="sr-only">Buscar productos, servicios o proveedores</span>
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6e6e73]" />
                <input
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Busca un producto, servicio o proveedor"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-[#d2d2d7] text-sm text-black placeholder-[#a1a1a6] outline-none focus:border-black"
                />
              </label>
              <button
                type="submit"
                className="px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-xl hover:bg-[#1d1d1f]"
              >
                Buscar proveedores
              </button>
              <Link
                href={`/buyer-dashboard?tab=ai-sourcing${searchText.trim() ? `&q=${encodeURIComponent(searchText.trim())}` : ''}`}
                className="flex items-center justify-center gap-2 px-5 py-2.5 border border-black text-black text-sm font-semibold rounded-xl hover:bg-[#f5f5f7]"
              >
                <Sparkles size={14} />
                Cotizar con IA
              </Link>
            </form>
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="border border-[#d2d2d7] rounded-2xl p-5 animate-pulse">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#f5f5f7] rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-[#f5f5f7] rounded w-3/4" />
                      <div className="h-3 bg-[#f5f5f7] rounded w-1/2" />
                    </div>
                  </div>
                  <div className="h-3 bg-[#f5f5f7] rounded w-full mb-2" />
                  <div className="h-3 bg-[#f5f5f7] rounded w-2/3" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && suppliers.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#f5f5f7] border border-[#e8e8ed] flex items-center justify-center mb-5">
                <Search size={24} className="text-[#a1a1a6]" />
              </div>
              <h3 className="text-base font-semibold text-black mb-2">
                {query ? `Sin resultados para "${query}"` : 'No hay proveedores registrados aún'}
              </h3>
              <p className="text-sm text-[#6e6e73] max-w-sm leading-relaxed mb-6">
                {query
                  ? 'Intenta con otros términos o explora las categorías disponibles.' :'Sé el primero en registrar tu empresa en MOONDEPTH.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link href="/" className="flex items-center gap-2 px-5 py-2.5 border border-[#d2d2d7] text-black text-sm font-semibold rounded-xl hover:border-black transition-all">
                  <ArrowLeft size={14} />
                  Volver al inicio
                </Link>
                <Link href="/sign-up-login" className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-semibold rounded-xl hover:bg-[#1d1d1f] transition-all">
                  Registrar mi empresa
                </Link>
              </div>
            </div>
          )}

          {/* Results grid */}
          {!loading && suppliers.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map(supplier => {
                const displayName = supplier.company_name || supplier.full_name || 'Proveedor';
                const initials = displayName.slice(0, 2).toUpperCase();
                const location = supplier.city && supplier.department
                  ? `${supplier.city}, ${supplier.department}`
                  : (supplier.city || supplier.department || 'Colombia');

                return (
                  <Link
                    key={supplier.id}
                    href={`/supplier-profile?id=${supplier.id}`}
                    className="group flex flex-col border border-[#d2d2d7] rounded-2xl p-5 hover:border-black hover:shadow-lg transition-all duration-200 bg-white"
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-[#f5f5f7] border border-[#e8e8ed] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {supplier.logo_url ? (
                          <img src={supplier.logo_url} alt={`Logo de ${displayName}`} className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <span className="text-sm font-bold text-black">{initials}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <h3 className="text-sm font-bold text-black truncate">{displayName}</h3>
                          {supplier.admin_verified && (
                            <BadgeCheck size={14} className="text-black flex-shrink-0" />
                          )}
                        </div>
                        {supplier.industry && (
                          <p className="text-xs text-[#6e6e73] mt-0.5 truncate">{supplier.industry}</p>
                        )}
                      </div>
                    </div>

                    {(supplier.what_we_sell || supplier.description) ? (
                      <p className="text-xs text-[#6e6e73] leading-relaxed line-clamp-2 mb-3 flex-1">
                        {supplier.what_we_sell || supplier.description}
                      </p>
                    ) : (
                      <p className="text-xs text-[#a1a1a6] italic mb-3 flex-1">Sin descripción aún.</p>
                    )}

                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-[#f0f0f5]">
                      <span className="flex items-center gap-1 text-xs text-[#6e6e73]">
                        <MapPin size={10} />
                        {location}
                      </span>
                      {supplier.product_images && supplier.product_images.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-[#6e6e73]">
                          <Package size={10} />
                          {supplier.product_images.length} foto{supplier.product_images.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black/20 border-t-black rounded-full animate-spin" />
      </div>
    }>
      <SearchResults />
    </Suspense>
  );
}
